import { inject, Injectable, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { AgencyService } from '@agency';
import type { AgencyInfo } from '@agency';
import { lastValueFrom } from 'rxjs';

import {
  AVAILABLE_TRANSLATION_LOCALES,
  type TranslationLocale,
} from '../../i18n/translation-locale.constants';
import { SmartyAuthService } from './smarty-auth.service';
import { SsoNavigationService } from './sso-navigation.service';

@Injectable({
  providedIn: 'root',
})
export class AuthBootstrapService {
  private readonly authService = inject(SmartyAuthService);
  private readonly agencyService = inject(AgencyService);
  private readonly ssoNavigation = inject(SsoNavigationService);
  private readonly translateService = inject(TranslateService);
  private readonly initialAuthenticationSucceededState = signal(false);

  public readonly initialAuthenticationSucceeded =
    this.initialAuthenticationSucceededState.asReadonly();

  public async initialize(): Promise<void> {
    const exchangeToken = this.consumeExchangeToken();
    const storedToken = this.authService.getAccessToken();

    this.agencyService.clearAgencyInfo();

    try {
      const token = await this.resolveAccessToken(exchangeToken, storedToken);
      this.agencyService.setAgencyInfo(parseAgencyInfo(token));
    } catch (error) {
      this.clearAuthenticationState(error);
      this.ssoNavigation.redirectToSso();
      return;
    }

    await this.initializeTranslations(this.agencyService.costaClickCulture());
    this.initialAuthenticationSucceededState.set(true);
  }

  private async resolveAccessToken(
    exchangeToken: string | undefined,
    storedToken: string | null,
  ): Promise<string> {
    if (exchangeToken) {
      const response = await lastValueFrom(this.authService.exchange(exchangeToken));
      const token = requireAccessToken(response.token);
      this.authService.setAccessToken(token);
      return token;
    }

    if (storedToken) {
      await lastValueFrom(
        this.authService.validateSession({
          skipAuthenticationRedirect: true,
        }),
      );
      return requireAccessToken(this.authService.getAccessToken());
    }

    return requireAccessToken(await lastValueFrom(this.authService.refreshSession()));
  }

  private async initializeTranslations(locale: TranslationLocale): Promise<void> {
    this.translateService.addLangs([...AVAILABLE_TRANSLATION_LOCALES]);
    await lastValueFrom(this.translateService.use(locale));
  }

  private clearAuthenticationState(error: unknown): void {
    this.authService.clearAccessToken();
    this.agencyService.clearAgencyInfo();
    console.error('Smarty authentication bootstrap failed.', error);
  }

  private consumeExchangeToken(): string | undefined {
    const url = new URL(window.location.href);
    const exchangeToken = url.searchParams.get('t')?.trim() || undefined;

    if (url.searchParams.has('t')) {
      url.searchParams.delete('t');
      window.history.replaceState(
        window.history.state,
        '',
        `${url.pathname}${url.search}${url.hash}`,
      );
    }

    return exchangeToken;
  }
}

function requireAccessToken(token: string | null | undefined): string {
  if (!token?.length) {
    throw new Error('Authentication response did not contain an access token.');
  }

  return token;
}

function parseAgencyInfo(token: string): AgencyInfo {
  const encodedPayload = token.split('.')[1];
  if (!encodedPayload) {
    throw new Error('Invalid JWT: payload is missing.');
  }

  const base64 = encodedPayload
    .replace(/-/g, '+')
    .replace(/_/g, '/')
    .padEnd(Math.ceil(encodedPayload.length / 4) * 4, '=');
  const bytes = Uint8Array.from(atob(base64), (character) => character.charCodeAt(0));
  const payload = JSON.parse(new TextDecoder().decode(bytes)) as unknown;
  const agencyId =
    typeof payload === 'object' && payload !== null
      ? (payload as Record<string, unknown>)['AgencyId']
      : undefined;

  if (typeof agencyId !== 'string' || !agencyId.trim()) {
    throw new Error('Invalid JWT: agency payload is missing.');
  }

  return payload as AgencyInfo;
}
